import React, { useEffect, useRef, useState } from "react";
import SoundPlayer from "./SoundPlayer";
import "./styles/DotMovement.css";

const DotMovement = ({
  dotColor,
  dotSize,
  bgColor,
  dotSpeed,
  dotGlow,
  dotBrightness = 1.0,
  soundFile,
  mute,
  volume = 0.5,
  isPaused,
  shouldRun,
  onCompletion,
}) => {
  const dotRef = useRef(null);
  const soundPlayerRef = useRef(null);
  const [cycles, setCycles] = useState(0);
  const [oldSpeed, setOldSpeed] = useState(dotSpeed);

  const stateRef = useRef({ shouldRun, mute, isPaused });
  stateRef.current = { shouldRun, mute, isPaused };

  const singleBounceDuration = (oldSpeed / 2).toFixed(3);
  const singleBounceDurationRef = useRef(singleBounceDuration);
  singleBounceDurationRef.current = singleBounceDuration;

  const currentAnimationRef = useRef(null);
  const rightTimeoutRef = useRef(null);
  const speedTimeoutRef = useRef(null);

  useEffect(() => {
    const currentDot = dotRef.current;
    if (currentDot && shouldRun) {
      const newAnimation = `moveDot ${singleBounceDuration * 2}ms linear infinite`;
      if (currentAnimationRef.current !== newAnimation) {
        currentDot.style.animation = newAnimation;
        currentAnimationRef.current = newAnimation;
      }
      currentDot.style.animationPlayState = isPaused ? "paused" : "running";
    }
    if (!shouldRun && currentAnimationRef.current) {
      currentAnimationRef.current = null;
    }
  }, [shouldRun, isPaused, singleBounceDuration]);

  useEffect(() => {
    if (oldSpeed !== dotSpeed) {
      if (speedTimeoutRef.current) clearTimeout(speedTimeoutRef.current);
      speedTimeoutRef.current = setTimeout(() => {
        setOldSpeed(dotSpeed);
        speedTimeoutRef.current = null;
      }, 600);
    }
  }, [dotSpeed, oldSpeed]);

  useEffect(() => {
    const currentDot = dotRef.current;
    if (!currentDot) return;

    const playLeft = () => soundPlayerRef.current?.play(-1);
    const playRight = () => soundPlayerRef.current?.play(1);

    const scheduleRight = () => {
      if (rightTimeoutRef.current) clearTimeout(rightTimeoutRef.current);
      rightTimeoutRef.current = setTimeout(() => {
        rightTimeoutRef.current = null;
        const { shouldRun: sr, mute: m, isPaused: p } = stateRef.current;
        if (sr && !m && !p) playRight();
      }, Number(singleBounceDurationRef.current));
    };

    const handleAnimationStart = (e) => {
      if (e.animationName !== "moveDot") return;
      const { shouldRun: sr, mute: m } = stateRef.current;
      if (!sr) return;
      if (!m) playLeft();
      scheduleRight();
    };

    const handleAnimationIteration = (e) => {
      if (e.animationName !== "moveDot") return;
      const { shouldRun: sr, mute: m, isPaused: p } = stateRef.current;
      if (p || !sr) return;
      setCycles((c) => c + 1);
      if (!m) playLeft();
      scheduleRight();
    };

    currentDot.addEventListener("animationstart", handleAnimationStart);
    currentDot.addEventListener("animationiteration", handleAnimationIteration);

    return () => {
      currentDot.removeEventListener("animationstart", handleAnimationStart);
      currentDot.removeEventListener("animationiteration", handleAnimationIteration);
      if (rightTimeoutRef.current) {
        clearTimeout(rightTimeoutRef.current);
        rightTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if ((isPaused || !shouldRun) && rightTimeoutRef.current) {
      clearTimeout(rightTimeoutRef.current);
      rightTimeoutRef.current = null;
    }
  }, [isPaused, shouldRun]);

  useEffect(() => {
    if (cycles >= 42) {
      if (onCompletion) onCompletion();
      setCycles(0);
    }
  }, [cycles, onCompletion]);

  const glowShadow = dotGlow
    ? `0 0 ${Math.round(dotSize * 0.6)}px ${dotGlow}`
    : "none";

  return (
    <div
      className="dot-movement-container"
      style={{
        backgroundColor: bgColor,
        '--dot-size': `${dotSize}px`,
      }}
    >
      <div
        ref={dotRef}
        className="dot"
        style={{
          width: `${dotSize}px`,
          height: `${dotSize}px`,
        }}
      >
        <div
          className="dot-visual dot-breathing"
          style={{
            background: `radial-gradient(circle at ${dotSize * 0.3}px ${dotSize * 0.3}px, ${dotColor}, #000)`,
            boxShadow: glowShadow,
            opacity: dotBrightness,
          }}
        />
      </div>
      <SoundPlayer
        ref={soundPlayerRef}
        soundFile={soundFile}
        mute={mute}
        volume={volume}
      />
    </div>
  );
};

export default DotMovement;
